import { useNavigate, useParams } from "react-router-dom";

import dayjs from "dayjs";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  FileText,
  MapPin,
  Package,
  PackageX,
  RotateCcw,
  Trash2,
  Truck,
  User,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Separator } from "~/components/ui/separator";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useReturnRequest, useReturnRequestItems } from "~/hooks/wms";
import type { DispositionType, ReturnRequestStatus } from "~/types/returns";
import {
  DISCARD_REASON_LABELS,
  DISPOSITION_COLORS,
  DISPOSITION_LABELS,
  RETURN_ITEM_STATUS_LABELS,
  RETURN_REASON_LABELS,
  RETURN_REQUEST_STATUS_LABELS,
  RETURN_TYPE_LABELS,
} from "~/types/returns";

const statusConfig: Record<
  ReturnRequestStatus,
  {
    icon: React.ElementType;
    variant: "default" | "secondary" | "destructive" | "outline";
    color: string;
  }
> = {
  pending: { icon: Clock, variant: "secondary", color: "text-yellow-500" },
  receiving: { icon: PackageX, variant: "default", color: "text-blue-500" },
  inspecting: { icon: Package, variant: "default", color: "text-purple-500" },
  routing: { icon: MapPin, variant: "default", color: "text-orange-500" },
  completed: {
    icon: CheckCircle2,
    variant: "outline",
    color: "text-green-500",
  },
  cancelled: {
    icon: AlertTriangle,
    variant: "destructive",
    color: "text-red-500",
  },
};

const dispositionIcons: Record<DispositionType, React.ElementType> = {
  SELLABLE: RotateCcw,
  REPACKAGE: Package,
  DISCARD: Trash2,
  SUPPLIER_RETURN: Truck,
};

export function ReturnDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: returnRequest, isLoading: requestLoading } =
    useReturnRequest(id);
  const { data: items = [], isLoading: itemsLoading } =
    useReturnRequestItems(id);

  const isLoading = requestLoading || itemsLoading;

  if (isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </PageContainer>
    );
  }

  if (!returnRequest) {
    return (
      <PageContainer>
        <Card>
          <CardContent className="py-8 text-center">
            <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-yellow-500" />
            <p className="text-muted-foreground">Devolución no encontrada</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate("/wms/returns")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver a Devoluciones
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  const statusConf = statusConfig[returnRequest.status];
  const StatusIcon = statusConf.icon;

  // Calculate disposition summary
  const dispositionSummary: Record<DispositionType, number> = {
    SELLABLE: 0,
    REPACKAGE: 0,
    DISCARD: 0,
    SUPPLIER_RETURN: 0,
  };

  items.forEach((item) => {
    item.inspections?.forEach((insp) => {
      dispositionSummary[insp.disposition] += insp.quantity;
    });
  });

  const totalDisposed = Object.values(dispositionSummary).reduce(
    (a, b) => a + b,
    0
  );

  return (
    <PageContainer>
      {/* Header */}
      <header className="space-y-3">
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2 h-9 text-muted-foreground"
          onClick={() => navigate("/wms/returns")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver
        </Button>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-xl font-bold">
                {returnRequest.return_number}
              </h1>
              <Badge variant={statusConf.variant}>
                <StatusIcon className="mr-1 h-3 w-3" />
                {RETURN_REQUEST_STATUS_LABELS[returnRequest.status]}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              {returnRequest.client?.name || "Sin cliente"} •{" "}
              {RETURN_TYPE_LABELS[returnRequest.return_type]}
            </p>
          </div>

          {/* Action buttons based on status */}
          <div className="flex items-center gap-2 sm:shrink-0">
            {returnRequest.status === "pending" && (
              <Button onClick={() => navigate(`/wms/returns/${id}/receive`)}>
                Iniciar Recepción
              </Button>
            )}
            {returnRequest.status === "receiving" && (
              <Button onClick={() => navigate(`/wms/returns/${id}/receive`)}>
                Continuar Recepción
              </Button>
            )}
            {returnRequest.status === "inspecting" && (
              <Button onClick={() => navigate(`/wms/returns/${id}/inspect`)}>
                Continuar Inspección
              </Button>
            )}
            {returnRequest.status === "routing" && (
              <Button onClick={() => navigate(`/wms/returns/${id}/putaway`)}>
                Continuar Ubicación
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Info Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4" />
              Información General
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Cliente
                </p>
                <p className="font-medium">
                  {returnRequest.client?.name || "-"}
                </p>
                {returnRequest.client?.rut && (
                  <p className="text-xs text-muted-foreground">
                    {returnRequest.client.rut}
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Tipo
                </p>
                <p className="font-medium">
                  {RETURN_TYPE_LABELS[returnRequest.return_type]}
                </p>
              </div>
            </div>

            {returnRequest.credit_note_number && (
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Nota de Crédito
                </p>
                <Badge variant="secondary">
                  {returnRequest.credit_note_number}
                </Badge>
              </div>
            )}

            {returnRequest.original_sales_request?.codigo && (
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Venta Original
                </p>
                <Badge variant="outline">
                  {returnRequest.original_sales_request.codigo}
                </Badge>
              </div>
            )}

            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Prioridad
              </p>
              <Badge
                variant={
                  returnRequest.priority === 1 ? "destructive" : "secondary"
                }
              >
                {returnRequest.priority === 1
                  ? "Urgente"
                  : returnRequest.priority === 3
                    ? "Baja"
                    : "Normal"}
              </Badge>
            </div>

            {returnRequest.notes && (
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Notas
                </p>
                <p className="text-sm">{returnRequest.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="h-4 w-4" />
              Fechas y Asignación
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Creado
                </p>
                <p className="text-sm">
                  {dayjs(returnRequest.created_at).format("DD/MM/YYYY HH:mm")}
                </p>
              </div>
              {returnRequest.started_at && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Iniciado
                  </p>
                  <p className="text-sm">
                    {dayjs(returnRequest.started_at).format("DD/MM/YYYY HH:mm")}
                  </p>
                </div>
              )}
            </div>

            {returnRequest.completed_at && (
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Completado
                </p>
                <p className="text-sm">
                  {dayjs(returnRequest.completed_at).format("DD/MM/YYYY HH:mm")}
                </p>
              </div>
            )}

            {returnRequest.assigned_profile && (
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Asignado a
                </p>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {returnRequest.assigned_profile.name}{" "}
                    {returnRequest.assigned_profile.lastname}
                  </span>
                </div>
                {returnRequest.assigned_at && (
                  <p className="text-xs text-muted-foreground">
                    Asignado el{" "}
                    {dayjs(returnRequest.assigned_at).format(
                      "DD/MM/YYYY HH:mm"
                    )}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Disposition Summary */}
      {totalDisposed > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Resumen de Disposiciones
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {(
                Object.entries(dispositionSummary) as [
                  DispositionType,
                  number,
                ][]
              )
                .filter(([, qty]) => qty > 0)
                .map(([disp, qty]) => {
                  const Icon = dispositionIcons[disp];
                  return (
                    <div
                      key={disp}
                      className={`flex items-center gap-3 rounded-lg border p-3 ${DISPOSITION_COLORS[disp]}`}
                    >
                      <Icon className="h-5 w-5" />
                      <div>
                        <p className="text-xs">{DISPOSITION_LABELS[disp]}</p>
                        <p className="text-xl font-bold">{qty}</p>
                      </div>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Items Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Package className="h-4 w-4" />
            Productos ({items.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Producto</TableHead>
                <TableHead>Razón</TableHead>
                <TableHead>Esperado</TableHead>
                <TableHead>Recibido</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Disposición</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item, index) => {
                const inspectionSummary = item.inspections?.reduce(
                  (acc, insp) => {
                    acc[insp.disposition] =
                      (acc[insp.disposition] || 0) + insp.quantity;
                    return acc;
                  },
                  {} as Record<DispositionType, number>
                );

                return (
                  <TableRow
                    key={item.id}
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {item.product?.short_name || "-"}
                        </p>
                        {item.original_lot_number && (
                          <p className="text-xs text-muted-foreground">
                            Lote: {item.original_lot_number}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm">
                          {RETURN_REASON_LABELS[item.return_reason]}
                        </p>
                        {item.return_reason_notes && (
                          <p className="text-xs text-muted-foreground">
                            {item.return_reason_notes}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{item.expected_quantity}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {item.received_quantity}
                        {item.received_quantity !== item.expected_quantity &&
                          item.status !== "pending" && (
                            <AlertTriangle className="h-4 w-4 text-yellow-500" />
                          )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          item.status === "routed"
                            ? "outline"
                            : item.status === "pending"
                              ? "secondary"
                              : "default"
                        }
                      >
                        {RETURN_ITEM_STATUS_LABELS[item.status]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {inspectionSummary &&
                      Object.keys(inspectionSummary).length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {(
                            Object.entries(inspectionSummary) as [
                              DispositionType,
                              number,
                            ][]
                          ).map(([disp, qty]) => {
                            const Icon = dispositionIcons[disp];
                            return (
                              <Badge
                                key={disp}
                                variant="outline"
                                className={DISPOSITION_COLORS[disp]}
                              >
                                <Icon className="mr-1 h-3 w-3" />
                                {qty}
                              </Badge>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detailed Inspections */}
      {items.some(
        (item) => item.inspections && item.inspections.length > 0
      ) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Detalle de Inspecciones</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {items
                .filter(
                  (item) => item.inspections && item.inspections.length > 0
                )
                .map((item) => (
                  <div key={item.id}>
                    <p className="mb-2 font-medium">
                      {item.product?.short_name || "Producto"}
                    </p>
                    <div className="space-y-2 pl-4">
                      {item.inspections?.map((insp) => {
                        const Icon = dispositionIcons[insp.disposition];
                        return (
                          <div
                            key={insp.id}
                            className="flex items-start justify-between rounded-lg border p-3"
                          >
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Badge
                                  variant="outline"
                                  className={
                                    DISPOSITION_COLORS[insp.disposition]
                                  }
                                >
                                  <Icon className="mr-1 h-3 w-3" />
                                  {DISPOSITION_LABELS[insp.disposition]}
                                </Badge>
                                <span className="text-sm font-medium">
                                  {insp.quantity} unidades
                                </span>
                              </div>
                              {insp.discard_reason && (
                                <p className="text-xs text-muted-foreground">
                                  Razón:{" "}
                                  {DISCARD_REASON_LABELS[
                                    insp.discard_reason as keyof typeof DISCARD_REASON_LABELS
                                  ] || insp.discard_reason}
                                </p>
                              )}
                              {insp.destination_position && (
                                <p className="text-xs text-muted-foreground">
                                  Ubicación:{" "}
                                  {insp.destination_position.full_code}
                                </p>
                              )}
                              {insp.notes && (
                                <p className="text-xs text-muted-foreground">
                                  Notas: {insp.notes}
                                </p>
                              )}
                            </div>
                            <div className="text-right text-xs text-muted-foreground">
                              {insp.inspector && (
                                <p>
                                  {insp.inspector.name}{" "}
                                  {insp.inspector.lastname}
                                </p>
                              )}
                              <p>
                                {dayjs(insp.inspected_at).format("DD/MM HH:mm")}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <Separator className="mt-4" />
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}

export default ReturnDetailPage;
